import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';
import { useAuth } from '../../../contexts/AuthContext';

const LoginForm = () => {
  const navigate = useNavigate();
  const { signIn, signInWithGoogle } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const mockCredentials = {
    clinician: { email: 'dr.smith@hospital.com', password: 'Clinical123!' },
    admin: { email: 'admin@diagnosticai.com', password: 'Admin123!' }
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex?.test(email)) {
      return 'Please enter a valid email address';
    }

    return null;
  };

  const validatePassword = (password) => {
    if (password?.length < 6) {
      return 'Password must be at least 6 characters';
    }
    return null;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (errors?.[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    const newErrors = {};
    const emailError = validateEmail(formData?.email);
    const passwordError = validatePassword(formData?.password);

    if (emailError) newErrors.email = emailError;
    if (passwordError) newErrors.password = passwordError;

    if (Object.keys(newErrors)?.length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      await signIn(formData?.email, formData?.password);
      navigate('/dashboard');
    } catch (error) {
      console.error('Login error:', error);
      setErrors({
        submit: error?.message || 'Failed to sign in. Please check your credentials and try again.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    setErrors({});

    try {
      await signInWithGoogle();
      // Note: User will be redirected by Supabase OAuth flow
    } catch (error) {
      console.error('Google sign-in error:', error);
      setErrors({
        submit: error?.message || 'Failed to sign in with Google. Please try again.'
      });
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="text-center mb-8">
        <div className="flex items-center justify-center gap-3 mb-3">
          <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="Activity" size={28} color="var(--color-primary)" />
          </div>
          <h1 className="text-2xl font-semibold text-foreground">DiagnosticAI</h1>
        </div>
        <p className="text-sm text-muted-foreground">
          Sign in to access evidence-based imaging recommendations
        </p>
      </div>

      <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
        <div className="flex items-start gap-2 mb-2">
          <Icon name="Info" size={16} color="var(--color-primary)" className="mt-0.5" />
          <p className="text-xs font-medium text-primary">Demo Credentials</p>
        </div>
        <div className="space-y-1 ml-6">
          <p className="text-xs text-muted-foreground">
            <span className="font-medium">Clinician:</span> {mockCredentials?.clinician?.email} / {mockCredentials?.clinician?.password}
          </p>
          


        </div>
      </div>

      {errors?.submit &&
      <div className="p-4 bg-error/10 border border-error/20 rounded-lg">
          <div className="flex items-start gap-3">
            <Icon name="AlertCircle" size={18} color="var(--color-error)" />
            <div className="flex-1">
              <p className="text-sm font-medium text-error">Authentication Failed</p>
              <p className="text-xs text-muted-foreground mt-1 whitespace-pre-line">{errors?.submit}</p>
            </div>
          </div>
        </div>
      }

      <div className="space-y-4">
        <Input
          label="Email Address"
          type="email"
          name="email"
          placeholder="doctor@hospital.com"
          value={formData?.email}
          onChange={handleChange}
          error={errors?.email}
          required
          disabled={isLoading} />


        <div className="relative">
          <Input
            label="Password"
            type={showPassword ? 'text' : 'password'}
            name="password"
            placeholder="Enter your password"
            value={formData?.password}
            onChange={handleChange}
            error={errors?.password}
            required
            disabled={isLoading} />

          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-9 p-1 rounded hover:bg-muted transition-clinical"
            disabled={isLoading}>

            <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={18} color="var(--color-muted-foreground)" />
          </button>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <Checkbox
          label="Remember me"
          name="rememberMe"
          checked={formData?.rememberMe}
          onChange={handleChange}
          disabled={isLoading} />

        <button
          type="button"
          onClick={() => navigate('/forgot-password')}
          className="text-sm font-medium text-primary hover:text-primary/80 transition-clinical"
          disabled={isLoading}>

          Forgot password?
        </button>
      </div>

      <Button
        type="submit"
        variant="default"
        fullWidth
        loading={isLoading}
        iconName="LogIn"
        iconPosition="right">

        Sign In
      </Button>

      <div className="relative my-6">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-border"></div>
        </div>
        <div className="relative flex justify-center text-xs">
          <span className="px-2 bg-background text-muted-foreground">New to DiagnosticAI?</span>
        </div>
      </div>

      <Button
        type="button"
        variant="outline"
        fullWidth
        onClick={() => navigate('/registration')}
        iconName="UserPlus"
        iconPosition="left"
        disabled={isLoading}>

        Create Account
      </Button>

      <div className="mt-6 p-3 bg-muted/50 rounded-lg border border-border">
        <div className="flex items-start gap-2">
          <Icon name="Shield" size={16} color="var(--color-primary)" className="mt-0.5" />
          <p className="text-xs text-muted-foreground">
            Your credentials are encrypted and protected with enterprise-grade security. We never share your information.
          </p>
        </div>
      </div>
    </form>);

};

export default LoginForm;